// src/context/WishlistContext.js
// Manages the customer's wishlist.
// Stored in localStorage keyed to the customer's Shopify ID so each
// account has its own wishlist on this device.
// Guests see a "Sign in to save" tooltip instead of being able to save.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const WishlistContext = createContext();

export function WishlistProvider({ children }) {
    const { customer, isLoggedIn } = useAuth();
    const [wishlist, setWishlist] = useState([]);

    // Derive a localStorage key from the customer's Shopify ID
    // Falls back to a guest key (which we never actually write to)
    const storageKey = customer?.id
        ? `maisondoud_wishlist_${customer.id}`
        : null;

    // Load wishlist from localStorage when the customer changes
    // (covers login, logout, and page refresh)
    useEffect(() => {
        if (storageKey) {
            try {
                const saved = localStorage.getItem(storageKey);
                const parsed = saved ? JSON.parse(saved) : [];
                // Valid JSON but not an array (e.g. "{}", "null") is just as
                // unusable as corrupted data — every consumer expects an array.
                setWishlist(Array.isArray(parsed) ? parsed : []);
            } catch {
                setWishlist([]);
            }
        } else {
            // Guest — clear wishlist state
            setWishlist([]);
        }
    }, [storageKey]);

    // Persist wishlist to localStorage whenever it changes
    useEffect(() => {
        if (storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(wishlist));
        }
    }, [wishlist, storageKey]);

    // Check if a product handle is in the wishlist
    const isWishlisted = (handle) => wishlist.includes(handle);

    // Add a product handle to the wishlist
    const addToWishlist = (handle) => {
        if (!isLoggedIn) return;
        setWishlist(prev => prev.includes(handle) ? prev : [...prev, handle]);
    };

    // Remove a product handle from the wishlist
    const removeFromWishlist = (handle) => {
        setWishlist(prev => prev.filter(h => h !== handle));
    };

    // Toggle — add if not saved, remove if already saved
    const toggleWishlist = (handle) => {
        if (!isLoggedIn) return false; // Signal to show tooltip
        if (isWishlisted(handle)) {
            removeFromWishlist(handle);
        } else {
            addToWishlist(handle);
        }
        return true;
    };

    return (
        <WishlistContext.Provider value={{
            wishlist,           // Array of product handles
            isWishlisted,       // (handle) => boolean
            toggleWishlist,     // (handle) => boolean (false = not logged in)
            removeFromWishlist,
            count: wishlist.length,
        }}>
            {children}
        </WishlistContext.Provider>
    );
}

export function useWishlist() {
    const context = useContext(WishlistContext);
    if (!context) throw new Error('useWishlist must be used within WishlistProvider');
    return context;
}